import { createAsyncThunk } from "@reduxjs/toolkit";

import * as basketAPI from "../../../api/basket";
import { IBasket } from "../../../models/catalogue";

/**
 * Parameters for loadBasketIfOutdated thunk.
 */
interface LoadBasketIfOutdatedParams {
    currentBasket: IBasket | null;
}

/**
 * Return type for loadBasketIfOutdated thunk.
 */
interface LoadBasketIfOutdatedResult {
    isOutdated: boolean;
    basket: IBasket;
}

/**
 * Check basket timestamp and reload if stale.
 *
 * Compares the current basket's last modified timestamp with the server's
 * timestamp. If the basket is outdated or missing, triggers a full basket reload
 * via the loadBasket thunk. This prevents unnecessary API calls when the basket
 * hasn't changed.
 *
 * @param {LoadBasketIfOutdatedParams} params - The basket checking parameters
 * @param {IBasket | null} params.currentBasket - The current basket in Redux state
 *
 * @returns {Promise<LoadBasketIfOutdatedResult>} Promise resolving to outdated status and basket data
 *
 * @example
 * // Check basket freshness on page focus
 * const { isOutdated, basket } = await dispatch(loadBasketIfOutdated({ currentBasket })).unwrap();
 * if (isOutdated) {
 *   console.log("Basket was reloaded");
 * }
 *
 * @sideEffects
 * - May dispatch `loadBasket` thunk if basket is outdated
 * - Updates basket data via extraReducers if reload occurs
 */
export const loadBasketIfOutdated = createAsyncThunk(
    "checkout/loadBasketIfOutdated",
    async (
        params: LoadBasketIfOutdatedParams,
        { dispatch },
    ): Promise<LoadBasketIfOutdatedResult> => {
        const { currentBasket } = params;

        if (
            !currentBasket ||
            !currentBasket.id ||
            !currentBasket.last_modified_timestamp
        ) {
            // Import dynamically to avoid circular dependency
            const { loadBasket } = await import("./loadBasket");
            const result = await dispatch(loadBasket()).unwrap();
            return {
                isOutdated: true,
                basket: result.basket,
            };
        }

        const data = await basketAPI.getLastModifiedTimestamp();
        if (!data || !data.last_modified_timestamp) {
            return {
                isOutdated: false,
                basket: currentBasket,
            };
        }

        const currentTimestamp = new Date(
            currentBasket.last_modified_timestamp,
        ).getTime();
        const newTimestamp = new Date(data.last_modified_timestamp).getTime();
        const idsMatch = currentBasket.id === data.id;
        const isOutdated = currentTimestamp < newTimestamp;

        if (currentTimestamp && newTimestamp && idsMatch && !isOutdated) {
            return {
                isOutdated: false,
                basket: currentBasket,
            };
        }

        // Import dynamically to avoid circular dependency
        const { loadBasket } = await import("./loadBasket");
        const result = await dispatch(loadBasket()).unwrap();
        return {
            isOutdated: true,
            basket: result.basket,
        };
    },
);
